/**
 * Custom Action - WHAT Section
 *
 * Edit mode UI for recurring message/report automation.
 * This is a complex action with multiple configuration options.
 *
 * V2 Reference: v2/app/templates/components/client-reminder-custom.hbs
 *
 * Fields:
 * - reminderType: Type of reason (workout, visit, assessment, custom)
 * - count: Number threshold
 * - timePeriod: Time period for the check (null/'' = within last period, 'all-time', 'always')
 * - reportType: Optional report to include (when reminderType is 'custom')
 * - reportFilters: Filter configuration for the selected report
 * - reminderTypeId: Assessment ID (when reminderType is assessment)
 * - hour: Hour of day to send (0-23)
 */

import { useQuery } from '@tanstack/react-query';
import { useFormikContext } from 'formik';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete } from 'src/components/Autocomplete';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Dialog } from 'src/components/Dialog';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import { fetchAssessmentOptions } from 'src/models/assessment/assessment';
import { Report } from 'src/models/report/_types';
import { fetchReport, fetchReportOptions } from 'src/models/report/report';
import { RangeTypeFilter } from 'src/shared/Filters/RangeTypeFilter';
import {
  ReportFilterConfig,
  buildReportFilterConfigs,
} from 'src/shared/Filters/buildReportFilterConfigs';
import { FormSelect } from 'src/shared/Forms/Fields/FormSelect';

import { ActionWhatSectionProps } from '../automationActions';


/**
 * Report Filter Dialog
 *
 * Renders report filter components directly in a dialog for configuration.
 * Uses local state so changes can be cancelled without affecting the automation.
 */
const ReportFilterDialog: FC<{
  open: boolean;
  onClose: () => void;
  onSave: (filters: Record<string, any>) => void;
  report: Report;
  initialFilters: Record<string, any>;
}> = ({ open, onClose, onSave, report, initialFilters }) => {
  const { t } = useTranslation();
  const [localFilters, setLocalFilters] = useState<Record<string, any>>({});

  // Sync local state when dialog opens
  useEffect(() => {
    if (open) {
      setLocalFilters(
        initialFilters && typeof initialFilters === 'object' && !Array.isArray(initialFilters)
          ? { ...initialFilters }
          : {}
      );
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const filterConfigs: ReportFilterConfig[] = buildReportFilterConfigs(
    report.filters || [],
    {
      report,
      reportConfigFilters: report.filters,
    }
  ).map((config) =>
    // Replace date picker with a range type selector for automations —
    // dates are computed at send time from the relative range type.
    config.field === 'date'
      ? {
          ...config,
          field: 'rangeType',
          label: 'Date Range',
          Component: (props: any) => (
            <RangeTypeFilter {...props} past label="Date Range" />
          ),
        }
      : config
  );

  const handleFilterChange = (field: string, value: any) => {
    setLocalFilters((prev) => ({ ...prev, [field]: value }));
  };

  // Mimic getFilterParamValue behavior from useListSearchParams:
  // URL params store everything as strings, and getFilterParamValue does
  // JSON.parse which converts "514" → 514 (number). Filter components
  // rely on this (e.g. GroupFilter does group.id === value with strict ===).
  // Since our values come directly from the server (not URL params), we
  // need to apply the same parse step.
  const getFilterValue = (field: string) => {
    const value = localFilters[field];
    if (value == null) return value;
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    }
    return value;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('automations.custom.reportFilters', 'Report Filters')}
      maxWidth="sm"
      fullWidth
      actions={[
        {
          label: t('automations.common.cancel', 'Cancel'),
          onClick: onClose,
        },
        {
          label: t('automations.common.save', 'Save'),
          onClick: () => onSave(localFilters),
          variant: 'prominent',
        },
      ]}
    >
      <Stack spacing={3}>
        {filterConfigs.map((filter) => (
          <Box key={filter.field}>
            <filter.Component
              setValue={(value: any) => handleFilterChange(filter.field, value)}
              value={getFilterValue(filter.field)}
              field={filter.field}
              label={filter.label}
              valueOnly={false}
              {...filter.props}
            />
          </Box>
        ))}
        {filterConfigs.length === 0 && (
          <Typography color="text.secondary">
            {t('automations.custom.noFiltersAvailable', 'No filters available for this report.')}
          </Typography>
        )}
      </Stack>
    </Dialog>
  );
};

/**
 * Custom WHAT Section - Edit mode
 */
export const CustomWhatSection: FC<ActionWhatSectionProps> = ({
  automation,
  index,
}) => {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);

  const reasonOptions = [
    { value: 'workout', label: t('automations.custom.workout') },
    { value: 'visit', label: t('automations.custom.visit') },
    { value: 'stat', label: t('automations.custom.measurement') },
    { value: 'progress', label: t('automations.custom.progressPhoto') },
    { value: 'assessment', label: t('automations.custom.assessment') },
    { value: 'custom', label: t('automations.custom.customMessage') },
  ];

  // V2 uses: null = "within the last period", 'all-time', 'always'
  // We use '' (empty string) instead of null for FormSelect compatibility
  const timePeriodOptions = [
    { value: 'always', label: t('automations.custom.alwaysSendReminder') },
    { value: 'all-time', label: t('automations.custom.atAnyTime') },
    { value: '', label: t('automations.custom.withinLastPeriod') },
  ];

  // Normalize timePeriod for the select: null/undefined should map to ''
  const normalizedTimePeriod =
    automation.timePeriod === null || automation.timePeriod === undefined
      ? ''
      : automation.timePeriod;

  // Fetch assessments for assessment type selection
  const { data: assessments = [], isLoading: assessmentsLoading } = useQuery({
    queryKey: ['assessments', 'options'],
    queryFn: fetchAssessmentOptions,
    staleTime: 60000,
  });

  const assessmentOptions = assessments.map((a) => ({
    value: a.id.toString(),
    label: a.name,
  }));

  // Fetch reports for report selection (only when reminderType is 'custom')
  const isCustomType = automation.reminderType === 'custom';
  const { data: reports = [], isLoading: reportsLoading } = useQuery({
    queryKey: ['reports', 'options'],
    queryFn: fetchReportOptions,
    staleTime: 60000,
    enabled: isCustomType,
  });

  const REPORT_TYPE_LABELS: Record<string, string> = {
    revenue: 'Revenue',
    client: 'Client',
    trainer: 'Staff',
    attendance: 'Attendance',
  };

  const reportOptions = reports
    .map((r) => ({
      value: r.id.toString(),
      label: r.name,
      group: REPORT_TYPE_LABELS[r.reportType] || r.reportType || 'Other',
    }))
    .sort((a, b) => a.group.localeCompare(b.group) || a.label.localeCompare(b.label));

  // Fetch the full report details (with filters) when a report is selected
  const selectedReportType = automation.reportType
    ? String(automation.reportType)
    : null;

  const { data: selectedReport } = useQuery({
    queryKey: ['report', selectedReportType],
    queryFn: () => fetchReport(selectedReportType!, { no_date: true }),
    staleTime: 60000,
    enabled: isCustomType && !!selectedReportType,
  });

  const isAssessmentType = automation.reminderType === 'assessment';
  const showCount = !isCustomType && normalizedTimePeriod !== 'always';

  const handleSaveFilters = (filters: Record<string, any>) => {
    setFieldValue(`automations.${index}.reportFilters`, filters);
    setFilterDialogOpen(false);
  };

  return (
    <Stack spacing={2}>
      {/* Reason Type */}
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Box sx={{ minWidth: 150 }}>
          <FormSelect
            name={`automations.${index}.reminderType`}
            options={reasonOptions}
            prompt={t('automations.custom.selectReason')}
            onChange={(e) => {
              setFieldValue(`automations.${index}.reminderType`, e.target.value);
              // Clear reminderTypeId when changing type away from assessment
              if (e.target.value !== 'assessment') {
                setFieldValue(`automations.${index}.reminderTypeId`, null);
              }
              // Clear report when changing away from custom
              if (e.target.value !== 'custom') {
                setFieldValue(`automations.${index}.reportType`, null);
                setFieldValue(`automations.${index}.reportFilters`, {});
              }
            }}
          />
        </Box>
      </Box>

      {/* Count and Time Period - only for non-custom types */}
      {!isCustomType && (
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          {showCount && (
            <>
              <Box>{t('automations.custom.hasNotCompleted')}</Box>
              <TextField
                type="number"
                value={automation.count || 0}
                onChange={(e) =>
                  setFieldValue(
                    `automations.${index}.count`,
                    parseInt(e.target.value) || 0
                  )
                }
                sx={{ width: 80 }}
                size="small"
                InputProps={{ inputProps: { min: 0 } }}
              />
              <Box>{t(`automations.custom.types.${automation.reminderType || 'workout'}`)}</Box>
            </>
          )}

          {/* Assessment selector - V2 shows this when reminderType is 'assessment' */}
          {isAssessmentType && (
            <Box sx={{ minWidth: 200 }}>
              <FormSelect
                name={`automations.${index}.reminderTypeId`}
                options={assessmentOptions}
                prompt={
                  assessmentsLoading
                    ? t('automations.common.loading')
                    : t('automations.custom.selectAssessmentOptional')
                }
              />
            </Box>
          )}

          <Box sx={{ minWidth: 180 }}>
            <TextField
              size="medium"
              value={normalizedTimePeriod}
              options={timePeriodOptions}
              onChange={(e) => {
                // Store null if user selects '' (within last period) to match v2
                const value = e.target.value === '' ? null : e.target.value;
                setFieldValue(`automations.${index}.timePeriod`, value);
              }}
            />
          </Box>
        </Box>
      )}

      {/* Custom message note */}
      {isCustomType && (
        <Typography color="text.secondary" fontStyle="italic">
          {t('automations.custom.configureMessageBelow')}
        </Typography>
      )}

      {/* Report selection - only for custom type */}
      {isCustomType && (
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Box>{t('automations.custom.includeReport', 'Include report?')}</Box>
          <Box sx={{ minWidth: 300 }}>
            <Autocomplete
              options={reportOptions}
              groupBy={(option) => option.group}
              getOptionLabel={(option) =>
                typeof option === 'string' ? option : option.label
              }
              value={
                reportOptions.find(
                  (o) => o.value === String(automation.reportType ?? '')
                ) || null
              }
              onChange={(_, newValue) => {
                const value = newValue?.value || null;
                setFieldValue(`automations.${index}.reportType`, value);
                setFieldValue(`automations.${index}.reportFilters`, {});
                if (value === 'custom_business_metrics') {
                  setFieldValue(`automations.${index}.media`, ['email']);
                }
              }}
              loading={reportsLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder={
                    reportsLoading
                      ? t('automations.common.loading', 'Loading...')
                      : t('automations.custom.selectReport', 'Select Report')
                  }
                />
              )}
              isOptionEqualToValue={(option, val) => option.value === val.value}
            />
          </Box>
          {automation.reportType && (
            <Button
              variant="borderless"
              size="small"
              onClick={() => setFilterDialogOpen(true)}
            >
              {t('automations.custom.editFilters', 'Edit filters')}
            </Button>
          )}
        </Box>
      )}

      {/* Report Filter Dialog */}
      {isCustomType && selectedReport && (
        <ReportFilterDialog
          open={filterDialogOpen}
          onClose={() => setFilterDialogOpen(false)}
          onSave={handleSaveFilters}
          report={selectedReport}
          initialFilters={automation.reportFilters || {}}
        />
      )}
    </Stack>
  );
};

/**
 * Custom What Summary Component
 */
export const CustomWhatSummary: FC<{
  automation: ActionWhatSectionProps['automation'];
}> = ({ automation }) => {
  const { t } = useTranslation();
  const isCustom = automation.reminderType === 'custom';
  const isAssessment = automation.reminderType === 'assessment';

  // Fetch assessments to display the name
  const { data: assessments = [] } = useQuery({
    queryKey: ['assessments', 'options'],
    queryFn: fetchAssessmentOptions,
    staleTime: 60000,
    enabled: isAssessment && !!automation.reminderTypeId,
  });

  // Fetch reports to display the selected report name
  const { data: reports = [] } = useQuery({
    queryKey: ['reports', 'options'],
    queryFn: fetchReportOptions,
    staleTime: 60000,
    enabled: isCustom && !!automation.reportType,
  });

  const selectedReport = reports.find(
    (r) => r.id.toString() === String(automation.reportType || '')
  );

  if (isCustom) {
    return (
      <>
        {t('automations.custom.customRecurringMessage')}
        {selectedReport && (
          <>
            {' '}
            {t('automations.custom.withReport', {
              reportName: selectedReport.name,
              defaultValue: 'with report {{reportName}}',
            })}
          </>
        )}
      </>
    );
  }

  const count = automation.count || 0;
  const type = automation.reminderType || 'workout';
  const timePeriod = automation.timePeriod;

  // V2 uses null for "within the last period"
  let periodText = '';
  if (timePeriod === 'always') {
    periodText = `- ${t('automations.custom.alwaysSendReminder').toLowerCase()}`;
  } else if (timePeriod === 'all-time') {
    periodText = t('automations.custom.atAnyTime').toLowerCase();
  } else {
    // null, undefined, or '' all mean "within the last period"
    periodText = t('automations.custom.withinLastPeriod').toLowerCase();
  }

  // Get assessment name if applicable
  const assessmentName = isAssessment && automation.reminderTypeId
    ? assessments.find((a) => a.id.toString() === automation.reminderTypeId?.toString())?.name
    : null;

  const typeText = t(`automations.custom.types.${type}`);
  return (
    <>
      {timePeriod !== 'always' && <>{t('automations.custom.hasNotCompleted')} </>}
      {count} {typeText}
      {assessmentName && <> ({assessmentName})</>}
      {' '}{periodText}
    </>
  );
};
